import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { batch } from "react-redux";
import { v4 } from "uuid";

import { useDispatch, useSelector } from "../../redux/hooks";
import { setEditing } from "../../redux/slices/entrySlice";
import {
  setGuernseyType,
  setNMEAModalData,
} from "../../redux/slices/generatorFeaturesSlice";
import {
  addNewDynamicRow,
  setLandscapeSection,
} from "../../redux/slices/generatorSlice";
import { insertValues } from "../../redux/utils/insertValues";
import { getRowWithExclusives } from "../../utils/logbookUtils/getRowWithExclusives";
import { type ILandscapeRow } from "../LandscapeOrientation/landscape.types";
import { type WarningModal } from "../PortraitOrientation/PortraitSection/AddDynamicRow/AddDynamicRow";
import {
  clearNonCopyable,
  clearRow,
  clearSingleValue,
} from "../utils/dynamicRows";
import { useTriggerAutoSave } from "./useTriggerAutoSave";
import useSecurityBagSearchValidation from "./useSecurityBagSearchValidation";

interface Props {
  copyPrevVal?: number;
  subSectionIndex?: number;
}

const getRowDates = () => {
  dayjs.extend(utc);
  const now = dayjs();
  const date = now.format();
  const dateUTC = now.utc().format();
  console.log({ date, dateUTC });
  return { date, dateUTC };
};

/**
 * Custom hook to add dynamic rows to a logbook section.
 *
 * @param props - An object containing the following properties:
 *   - copyPrevVal: An optional number indicating the value to copy from the previous row.
 *   - subSectionIndex: An optional number representing the index of the subsection.
 * @returns An object containing methods to add rows and manage warning modals.
 */
const useAddDynamicRow = ({ copyPrevVal, subSectionIndex }: Props) => {
  const triggerAutoSave = useTriggerAutoSave();
  const dispatch = useDispatch();
  const { getValues } = useFormContext();
  const { sections, showDateTimePerRow } = useSelector(
    state => state.generatorSlice.lbentry,
  );
  const { validateBagSearchRequired } = useSecurityBagSearchValidation();

  const [isWarningModalOpened, setIsWarningModalOpened] =
    useState<WarningModal | null>(null);
  /**
   * Adds a new row to a landscape-oriented section.
   *
   * @param sectionIndex - The index of the section to which the row will be added.
   * @param isCopyOn - A boolean indicating whether to copy values from the previous row.
   */
  const addLandscapeRow = (sectionIndex: number, isCopyOn = false) => {
    if (subSectionIndex === undefined) return;

    const data = getValues();
    // Performance optimization: only process the specific section we need instead of all sections
    const sectionToProcess = [sections[sectionIndex]];
    const modifiedLogbook: any = insertValues(data, sectionToProcess);
    const modifiedSection =
      modifiedLogbook?.[0]?.subSections?.[subSectionIndex];

    const lastCols = modifiedSection.rows.map((row: ILandscapeRow) =>
      Object.values(row.items).map(item => {
        const cols = Object.values(item.cols);
        return clearNonCopyable(cols[cols.length - 1]);
      }),
    );

    // Performance optimization: instead of cloning the entire section,
    // build only the new column data we need to add
    const newColsData: any = {};
    modifiedSection.rows.forEach((row: ILandscapeRow, rowIndex: number) => {
      Object.entries(row.items).forEach(([itemKey, item], itemIndex) => {
        if (!newColsData[rowIndex]) newColsData[rowIndex] = {};

        if (isCopyOn) {
          const newColId = v4();
          newColsData[rowIndex][itemKey] = {
            colId: newColId,
            colData: lastCols[rowIndex][itemIndex],
          };
        } else {
          const id = v4();
          const baseCol = {
            ...lastCols[rowIndex][itemIndex],
            value: clearSingleValue(lastCols[rowIndex][itemIndex]),
          };

          if (baseCol.cols) {
            const newNestedCols: any = {};
            Object.entries(baseCol.cols).forEach(([key, col]) => {
              const newCol = {
                ...(col as any),
                value: clearSingleValue(col as any),
              };
              newNestedCols[v4()] = newCol;
            });
            baseCol.cols = newNestedCols;
          }

          newColsData[rowIndex][itemKey] = {
            colId: id,
            colData: baseCol,
          };
        }
      });
    });

    // Now apply the new columns to a shallow copy of the section
    const section = {
      ...modifiedSection,
      rows: modifiedSection.rows.map(
        (row: ILandscapeRow, rowIndex: number) => ({
          ...row,
          items: {
            ...row.items,
            ...Object.fromEntries(
              Object.entries(row.items).map(([itemKey, item]) => [
                itemKey,
                {
                  ...item,
                  cols: {
                    ...item.cols,
                    [newColsData[rowIndex][itemKey].colId]:
                      newColsData[rowIndex][itemKey].colData,
                  },
                },
              ]),
            ),
          },
        }),
      ),
    };

    // Batch the landscape section dispatches
    batch(() => {
      dispatch(setLandscapeSection({ sectionIndex, subSectionIndex, section }));
      dispatch(setEditing(true));
    });
  };
  /**
   * Adds a new dynamic row to a section.
   *
   * @param sectionIndex - The index of the section to which the row will be added.
   * @param isCopyOn - A boolean indicating whether to copy values from the previous row.
   * @param omitWarn - An optional boolean to omit warning modals.
   * @param isSingleRowInCat - An optional boolean indicating if the section contains a single row in the category.
   */
  const addDynamicRow = useCallback(
    (
      sectionIndex: number,
      isCopyOn = false,
      omitWarn?: boolean,
      isSingleRowInCat?: boolean,
      addToTop = false,
    ) => {
      const section = sections[sectionIndex];
      if (!section) return;

      const validationResult = validateBagSearchRequired(sectionIndex);
      if (validationResult?.blocked) {
        setIsWarningModalOpened({
          type: "security",
          text: validationResult.message,
        });
        return;
      }

      if (section.rowLimit && section.rows.length === section.rowLimit) {
        setIsWarningModalOpened({ type: "limit" });
        return;
      }
      if (
        !omitWarn &&
        section.rowWarning &&
        section.rows.length === section.rowWarning
      ) {
        setIsWarningModalOpened({
          type: "warn",
          addNewRowProps: { sectionIndex, isCopyOn, omitWarn: true },
        });
        return;
      }
      const sourceRow = section.rows[section.rows.length - 1];
      if (!sourceRow) return;

      const shouldInsertValues =
        isCopyOn || (typeof copyPrevVal === "number" && !section.NMEA_button);

      let modifiedSection = {
        ...section,
        rows: [shouldInsertValues ? sourceRow : structuredClone(sourceRow)],
      };

      if (shouldInsertValues) {
        const data = getValues();
        const modifiedLogbook: any = insertValues(data, [modifiedSection]);
        const modifiedSingleRowSection = modifiedLogbook?.[0];
        if (!modifiedSingleRowSection?.rows?.length) return;
        modifiedSection = {
          ...section,
          rows: modifiedSingleRowSection.rows,
        };
      }

      if (!modifiedSection) return;
      const newRowWithNewExlusivesIds = getRowWithExclusives(
        modifiedSection,
        modifiedSection.rows.length - 1,
      );

      // Batch all Redux dispatches to minimize re-renders
      batch(() => {
        dispatch(setGuernseyType(null));
        if (isCopyOn) {
          if (section.showDateTimePerRow ?? showDateTimePerRow) {
            const { date, dateUTC } = getRowDates();
            newRowWithNewExlusivesIds.date = date;
            newRowWithNewExlusivesIds.dateUTC = dateUTC;
          }
          dispatch(
            addNewDynamicRow({
              row: newRowWithNewExlusivesIds,
              sectionIndex,
              skipSorting: section.NMEA_button,
              addToTop,
            }),
          );
        } else {
          // Scroll correction for smooth UX
          if (window) {
            const t = document.getElementById("page-container");
            if (t) {
              window.scSX = t.scrollLeft;
              window.scSY = t.scrollTop;
              window.pendingScrollCorrection = true;

              if (!window.registeredScrollCorrection) {
                t.addEventListener("scroll", () => {
                  if (window.pendingScrollCorrection && t) {
                    t.scroll(window.scSX, window.scSY);
                    window.pendingScrollCorrection = false;
                  }
                });
                window.registeredScrollCorrection = true;
              }
            }
          }

          const newRow = clearRow(
            newRowWithNewExlusivesIds as any,
            copyPrevVal,
            isSingleRowInCat,
            section.NMEA_button,
          );

          if (section.showDateTimePerRow ?? showDateTimePerRow) {
            const { date, dateUTC } = getRowDates();
            newRow.date = date;
            newRow.dateUTC = dateUTC;
          }

          if (section.NMEA_button) {
            // For NMEA sections, only open modal - don't add row yet
            // Row will be added when user clicks "Enter Manually" or when NMEA data is processed
            dispatch(
              setNMEAModalData({
                opened: true,
                rowIndex: modifiedSection.rows.length,
                triggeredBy: "add",
                sectionIndex,
                // Store the prepared row for manual entry
                pendingRow: newRow,
              }),
            );
          } else {
            // For non-NMEA sections, add the row immediately
            dispatch(
              addNewDynamicRow({
                row: newRow,
                sectionIndex,
                skipSorting: section.NMEA_button,
                addToTop,
              }),
            );
          }
        }
      });

      // Trigger auto-save after batched dispatches complete
      if (!section.NMEA_button) {
        triggerAutoSave();
      }
    },
    [
      sections,
      getValues,
      dispatch,
      triggerAutoSave,
      copyPrevVal,
      showDateTimePerRow,
      validateBagSearchRequired,
    ],
  );

  return {
    addDynamicRow,
    addLandscapeRow,
    isWarningModalOpened,
    setIsWarningModalOpened,
  };
};
export default useAddDynamicRow;
